<template>
  <avs-card>
    <template v-slot:title>
      <v-toolbar
        class="mt-4 custom-toolbar"
        elevation="0"
      >
        <v-text-field
          clearable
          class="col-md-8"
          hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('commons.search')"
          v-model="search"
        />
        <slot
          name="actions"
          v-bind:dataset="dataset"
        />
      </v-toolbar>
    </template>
    <template v-slot:content>
      <v-data-table
        class="ma-10 bordered-table"
        disable-pagination
        :no-data-text="$t('messages.information.no_data')"
        :headers="headers"
        hide-default-footer
        :items="dataset"
        :loading="loading"
        loading-text="Loading Pcq/Dsi"
        no-results-text="No Pcq/Dsi found"
        :search="search"
        @dblclick:row="($event, { item }) => toEdit(item)"
      >
        <template v-slot:item.task.name="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
              >
                {{ getFullName(item) }}
              </span>
            </template>
            <span>{{ getFullName(item) }}</span>
          </v-tooltip>
        </template>

        <template v-slot:[`item.performer`]="{ item }">
          <div v-if="item">
            <avs-chip
              v-if="item.performer_sign_date"
              color="info"
              iconColor="blue"
              text-color="blue"
            >
              {{ $t("commons.signed") }}
            </avs-chip>
            <v-btn
              v-else-if="needsActive(item, 'performer')"
              :disabled="!needsSignature(item, 'performer')"
              color="primary"
              @click="openSignatureModal(item, 'performer')"
            >
              {{ $t("commons.sign_here") }}
            </v-btn>
            <span
              v-else
              style="display: flex; justify-content: center; align-items: center; height: 100%"
            >
              /
            </span>
          </div>
        </template>
        <template v-slot:[`item.quality_control`]="{ item }">
          <div v-if="item">
            <avs-chip
              v-if="item.quality_control_sign_date"
              color="info"
              iconColor="blue"
              text-color="blue"
            >
              {{ $t("commons.signed") }}
            </avs-chip>
            <v-btn
              v-else-if="needsActive(item, 'quality_control')"
              :disabled="!needsSignature(item, 'quality_control')"
              color="primary"
              @click="openSignatureModal(item, 'quality_control')"
            >
              {{ $t("commons.sign_here") }}
            </v-btn>
            <span
              v-else
              style="display: flex; justify-content: center; align-items: center; height: 100%"
            >
              /
            </span>
          </div>
        </template>
        <template v-slot:item.technical_control="{ item }">
          <div v-if="item">
            <avs-chip
              v-if="item.technical_control_sign_date"
              color="info"
              iconColor="blue"
              text-color="blue"
            >
              {{ $t("commons.signed") }}
            </avs-chip>
            <v-btn
              v-else-if="needsActive(item, 'technical_control')"
              :disabled="!needsSignature(item, 'technical_control')"
              color="primary"
              @click="openSignatureModal(item, 'technical_control')"
            >
              {{ $t("commons.sign_here") }}
            </v-btn>
            <span
              v-else
              style="display: flex; justify-content: center; align-items: center; height: 100%"
            >
              /
            </span>
          </div>
        </template>
        <template v-slot:item.part_a="{ item }">
          <div v-if="item">
            <avs-chip
              v-if="item.part_a_sign_date"
              color="info"
              iconColor="blue"
              text-color="blue"
            >
              {{ $t("commons.signed") }}
            </avs-chip>
            <v-btn
              v-else-if="needsActive(item, 'part_a')"
              :disabled="!needsSignature(item, 'part_a')"
              color="primary"
              @click="openSignatureModal(item, 'part_a')"
            >
              {{ $t("commons.sign_here") }}
            </v-btn>
            <span
              v-else
              style="display: flex; justify-content: center; align-items: center; height: 100%"
            >
              /
            </span>
          </div>
        </template>

        <!-- Répétez le même schéma pour Part B, Part C, Part D -->
        <template v-slot:item.part_b="{ item }">
          <div v-if="item">
            <avs-chip
              v-if="item.part_b_sign_date"
              color="info"
              iconColor="blue"
              text-color="blue"
            >
              {{ $t("commons.signed") }}
            </avs-chip>
            <v-btn
              v-else-if="needsActive(item, 'part_b')"
              :disabled="!needsSignature(item, 'part_b')"
              color="primary"
              @click="openSignatureModal(item, 'part_b')"
            >
              {{ $t("commons.sign_here") }}
            </v-btn>
            <span
              v-else
              style="display: flex; justify-content: center; align-items: center; height: 100%"
            >
              /
            </span>
          </div>
        </template>

        <template v-slot:item.part_c="{ item }">
          <div v-if="item">
            <avs-chip
              v-if="item.part_c_sign_date"
              color="info"
              iconColor="blue"
              text-color="blue"
            >
              {{ $t("commons.signed") }}
            </avs-chip>
            <v-btn
              v-else-if="needsActive(item, 'part_c')"
              :disabled="!needsSignature(item, 'part_c')"
              color="primary"
              @click="openSignatureModal(item, 'part_c')"
            >
              {{ $t("commons.sign_here") }}
            </v-btn>
            <span
              v-else
              style="display: flex; justify-content: center; align-items: center; height: 100%"
            >
              /
            </span>
          </div>
        </template>

        <template v-slot:item.part_d="{ item }">
          <div v-if="item">
            <avs-chip
              v-if="item.part_d_sign_date"
              color="info"
              iconColor="blue"
              text-color="blue"
            >
              Signed
            </avs-chip>
            <v-btn
              v-else-if="needsActive(item, 'part_d')"
              :disabled="!needsSignature(item, 'part_d')"
              color="primary"
              @click="openSignatureModal(item, 'part_d')"
            >
              {{ $t("commons.sign_here") }}
            </v-btn>
            <span
              v-else
              style="display: flex; justify-content: center; align-items: center; height: 100%"
            >
              /
            </span>
          </div>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <avs-editButton
              v-if="!item.is_locked"
              :to="getToEditLink(item)"
              :spantext="$t('actions.edit', { entity: $t('categories.pcqdsi.name').toLowerCase() })"
            />
            <avs-deleteButton
              v-if="!item.is_locked"
              @click="handleOpenDeleteDialog(item)"
              :spantext="
                $t('actions.delete', { entity: $t('categories.pcqdsi.name').toLowerCase() })
              "
            />
          </div>
        </template>
      </v-data-table>
      <avs-deleteDialog
        v-model="deleteDialogIsOpen"
        @cancel="handleCancel"
        @delete="handleDelete"
        resource="pcq/dsi"
      />

      <v-dialog
        v-model="isSignatureModalOpen"
        max-width="500px"
        transition="none"
      >
        <v-card>
          <v-card-title>{{ $t("commons.signature") }}</v-card-title>
          <v-tabs v-model="activeTab">
            <v-tab>{{ $t("commons.simple_sign") }}</v-tab>
            <v-tab>{{ $t("commons.drawing_sign") }}</v-tab>
          </v-tabs>
          <v-tabs-items
            v-model="activeTab"
            eager
          >
            <!-- Onglet avec le bouton "Signer" -->
            <v-tab-item>
              <v-card-text>
                <!-- Message d'alerte -->
                <v-alert
                  color="secondary"
                  type="info"
                  outlined
                  class="mb-4"
                  border="left"
                >
                  {{ $t("commons.signature_already_saved") }}
                </v-alert>

                <!-- Bouton de signature -->
                <div class="text-center mt-5 mb-5">
                  <v-btn
                    color="primary"
                    @click="simpleSign"
                  >
                    {{ $t("commons.sign") }}
                  </v-btn>
                </div>
              </v-card-text>
            </v-tab-item>

            <!-- Onglet avec la zone de dessin -->
            <v-tab-item>
              <v-card-text>
                <v-alert
                  color="secondary"
                  type="info"
                  outlined
                  class="mb-4"
                  border="left"
                >
                  {{ $t("commons.signature_not_saved") }}
                </v-alert>

                <div class="text-center mt-5 mb-5">
                  <v-btn
                    color="primary"
                    @click="openCanvasModal"
                  >
                    {{ $t("commons.confirm_sign") }}
                  </v-btn>
                </div>
              </v-card-text>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-dialog>

      <!-- Modal pour le canvas uniquement -->
      <v-dialog
        v-model="isCanvasModalOpen"
        max-width="500px"
        transition="none"
      >
        <v-card>
          <v-card-title>{{ $t("commons.signature") }}</v-card-title>
          <v-card-text>
            <canvas
              ref="signatureCanvas"
              width="450"
              height="200"
              class="signature-canvas"
              @mousedown="startDrawing"
              @mousemove="draw"
              @mouseup="stopDrawing"
              @mouseleave="stopDrawing"
            ></canvas>
          </v-card-text>
          <v-card-actions>
            <v-btn
              color="primary"
              @click="saveSignature"
            >
              {{ $t("commons.sign") }}
            </v-btn>
            <v-btn
              color="secondary"
              @click="clearSignature"
            >
              {{ $t("commons.clear") }}
            </v-btn>
            <v-btn
              text
              @click="isCanvasModalOpen = false"
            >
              {{ $t("commons.close") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </avs-card>
</template>

<script>
import { PcqDsiService } from "@/services"
import { toDatetime } from "@/utils/datetime"

export default {
  name: "PcqDsisTable",
  props: {
    pcqDsiIsConfirm: {
      type: Boolean,
      required: false
    },
    pcqDsiIsCLocked: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    pcqLiner() {
      return this.$store.state.app.pcqLiner
    },
    project() {
      return this.$store.state.app.project
    }
  },
  data() {
    return {
      dataset: [],
      deleteDialogIsOpen: false,
      isSignatureModalOpen: false,
      isCanvasModalOpen: false,
      currentItem: null,
      currentSection: null,
      document: null,
      headers: [
        { text: "Revision", value: "revision", sortable: false },
        { text: "Step", value: "order", sortable: false },
        { text: "Task", value: "task.name", class: "min-width-200", sortable: false },
        { text: "ITNS", value: "pia", sortable: false },
        {
          text: "Applicable Document",
          value: "applicable_document",
          sortable: false
        },
        { text: "Doc. Rev.", value: "doc_rev", sortable: false },
        { text: "Performer", value: "performer", sortable: false },
        { text: "Quality Control", align: "center", value: "quality_control", sortable: false },
        {
          text: "Technical Control",
          sortable: false,
          value: "technical_control"
        },
        { text: "Quality point", align: "center", value: "quality_point_a", sortable: false },
        { text: "Party A", align: "center", value: "part_a", sortable: false },
        { text: "Quality point", align: "center", value: "quality_point_b", sortable: false },
        { text: "Party B", align: "center", value: "part_b", sortable: false },
        { text: "Quality point", align: "center", value: "quality_point_c", sortable: false },
        { text: "Party C", align: "center", value: "part_c", sortable: false },
        { text: "Quality point", align: "center", value: "quality_point_d" },
        { text: "Party D", align: "center", value: "part_d", sortable: false },
        { text: "Record required", align: "center", value: "record", sortable: false },
        { text: "Record reference", align: "center", value: "reference", sortable: false },
        {
          text: "NCR/DR other observation",
          align: "center",
          value: "observation",
          sortable: false
        },
        {
          text: "Actions",
          sortable: false,
          value: "actions",
          align: "right"
        }
      ],
      loading: false,
      search: "",
      selectedItem: null,
      template: null,
      values: null,
      activeTab: 0
    }
  },
  methods: {
    toDatetime,
    needsSignature(item, section) {
      const userId = this.$store.state.app.user.id
      // return item.signatures && item.signatures.some(signature => signature.section === section)
      return (
        item.signatures &&
        item.signatures.some(
          signature =>
            signature.section === section &&
            signature.assigned_user === userId &&
            this.pcqDsiIsConfirm
        )
      )
    },
    needsActive(item, section) {
      // return item.signatures && item.signatures.some(signature => signature.section === section)
      return item.signatures && item.signatures.some(signature => signature.section === section)
    },
    getDataset() {
      this.loading = true
      PcqDsiService.get(this, this.pcqLiner.id)
        .then(response => {
          this.dataset = response.results
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            // alert("Access denied: You do not have permission to access this resource.")
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    getToEditLink(item) {
      return {
        name: "PcqDsiCreateUpdate",
        params: { id: item.id }
      }
    },
    getTaskName(item) {
      return this.truncateText(item.task.name, 10)
        ? item.task.name
        : this.truncateText(item.name, 10)
    },
    getFullName(item) {
      return item.task.name ? item.task.name : item.name
    },
    truncateText(text, length) {
      return text.length > length ? text.substring(0, length) + "..." : text
    },
    handleOpenDeleteDialog(item) {
      this.deleteDialogIsOpen = true
      this.selectedItem = item
    },
    handleCancel() {
      this.deleteDialogIsOpen = false
    },
    handleDelete(item) {
      PcqDsiService.remove(this, this.selectedItem.id)
        .then(r => {
          this.dataset = this.dataset.filter(nc => nc.id !== this.selectedItem.id)
          this.$store.commit("app/message", {
            msg: this.$t("messages.success.deleted", {
              entity: this.$tc("categories.pcqdsi.name", 1)
            }),
            color: "success"
          })
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            // alert("Access denied: You do not have permission to access this resource.")
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
      this.deleteDialogIsOpen = false
    },
    handleStatusComponent(task) {
      return task.status
    },
    isMettingDisabled(item) {
      return item.status === "Closed"
    },
    toEdit(item) {
      this.$router.push(this.getToEditLink(item))
    },
    openCanvasModal() {
      this.isCanvasModalOpen = true
      this.isSignatureModalOpen = false
      this.$nextTick(() => {
        const canvas = this.$refs.signatureCanvas
        if (canvas) {
          this.context = canvas.getContext("2d")
          if (this.context) {
            this.context.lineWidth = 2
            this.context.lineCap = "round"
            this.context.strokeStyle = "black"
          } else {
            console.error("Impossible d'obtenir le contexte 2D !")
          }
        } else {
          console.error("Canvas introuvable !")
        }
      })
    },
    openSignatureModal(item, section) {
      this.currentItem = item
      this.currentSection = section
      this.isSignatureModalOpen = true
    },

    // Démarre le dessin
    startDrawing(event) {
      this.isDrawing = true
      this.context.beginPath()
      this.context.moveTo(event.offsetX, event.offsetY)
    },

    // Dessine sur le canvas
    draw(event) {
      if (!this.isDrawing) return
      this.context.lineTo(event.offsetX, event.offsetY)
      this.context.stroke()
    },

    // Arrête le dessin
    stopDrawing() {
      if (!this.isDrawing) return
      this.isDrawing = false
      this.context.closePath()
    },

    // Enregistre la signature
    saveSignature() {
      const canvas = this.$refs.signatureCanvas
      const signatureImageDataURL = canvas.toDataURL("image/png")
      // Appel API pour sauvegarder la signature (simplifié)

      // Convertir le Data URL en Blob pour l'envoi
      const signatureImageBlob = this.dataURLtoBlob(signatureImageDataURL)

      // Appel API pour sauvegarder la signature
      PcqDsiService.sign(this.currentItem.id, this.currentSection, signatureImageBlob)
        .then(response => {
          this.$store.commit("app/message", {
            msg: this.$t("messages.success.saved", { entity: this.$t("commons.signature") }),
            color: "success"
          })
          this.isSignatureModalOpen = false
          this.isCanvasModalOpen = false
          this.getDataset()
        })
        .catch(error => {
          if (error.response && error.response.status === 403) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          } else {
            let message = ""
            for (const e in error.response.data) {
              message += "- " + e + " : " + error.response.data[e] + "  "
            }
            // this.send(message, "error")
            this.$store.commit("app/message", {
              msg: message,
              color: "error"
            })
          }
        })
      // PcqDsiService.sign(this.currentItem.id, this.currentSection, signatureImage)
      // this.isSignatureModalOpen = false
    },

    simpleSign() {
      // Remplacez ceci par la logique pour une signature simple
      PcqDsiService.sign(this.currentItem.id, this.currentSection, null)
        .then(response => {
          this.$store.commit("app/message", {
            msg: this.$t("messages.success.saved", { entity: this.$t("commons.signature") }),
            color: "success"
          })
          this.isSignatureModalOpen = false
          this.getDataset()
        })
        .catch(error => {
          // Gérer les erreurs
          this.$store.commit("app/message", {
            msg: error.detail,
            color: "error"
          })
        })
    },

    dataURLtoBlob(dataURL) {
      const byteString = atob(dataURL.split(",")[1])
      const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0]
      const ab = new ArrayBuffer(byteString.length)
      const ia = new Uint8Array(ab)

      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
      }

      return new Blob([ab], { type: mimeString })
    },

    // Efface le contenu du canvas pour réinitialiser la signature
    clearSignature() {
      const canvas = this.$refs.signatureCanvas
      const context = canvas.getContext("2d")
      context.clearRect(0, 0, canvas.width, canvas.height)
    }
  },
  mounted() {
    // this.getDataset()
    if (!this.pcqLiner) {
      this.$router.push({ name: "PcqLiner" })
    } else {
      this.getDataset() // Si "pcqLiner" est valide, chargez les données
    }
  }
}
</script>

<style>
.custom-toolbar >>> .v-toolbar__content {
  gap: 1rem;
}
.bordered-table {
  border: 1px solid #dee2e6;
  border-radius: 10px; /* Arrondi des coins du tableau */
  /* border-radius: 4px; */
  border-collapse: collapse; /* Important pour avoir des bordures propres */
}

.bordered-table thead {
  background-color: #f8f9fa;
}

.bordered-table th,
.bordered-table td {
  border: 1px solid #dee2e6; /* Bordure entre les cellules */
  padding: 8px;
}

.signature-canvas {
  border: 1px solid #ccc;
  cursor: crosshair;
}

.signature-canvas {
  border: 1px solid #ccc;
  cursor: crosshair; /* Montre une croix pour indiquer une zone interactive */
  width: 100%;
  height: auto; /* S'assure que le canvas est bien dimensionné */
  touch-action: none; /* Désactive les gestes tactiles pour éviter des comportements inattendus */
}
</style>
